import { useEffect } from "react";
import { CheckCircle, ArrowRight, X, Wallet, Receipt, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openWhatsApp, receiptMessage } from "../lib/whatsapp";
import { ReceiptMenu } from "./ReceiptMenu";

const fmtBRL = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export const PaymentResultDialog = ({ summary, onClose }) => {
  useEffect(() => {
    if (!summary) return undefined;
    return () => {
      setTimeout(() => {
        if (!document.querySelector('[role="dialog"][data-state="open"]')) {
          document.body.style.pointerEvents = "";
        }
      }, 350);
    };
  }, [summary]);
  if (!summary) return null;
  const diff = Math.round(((summary.client_balance_before ?? 0) - (summary.client_balance_after ?? 0)) * 100) / 100;
  const withReceipt = (summary.orders || []).filter((o) => o.payment_id);
  const singleReceipt = withReceipt.length === 1 ? withReceipt[0] : null;
  const sendWhatsApp = () => {
    const opened = openWhatsApp(summary.client_phone, receiptMessage({
      clientName: summary.client_name,
      date: summary.payment_date,
      amount: summary.amount_received,
      method: summary.payment_method,
      orders: (summary.orders || []).map((o) => ({ order_code: o.order_code, amount: o.applied })),
      leftoverCredit: summary.leftover_credit || 0,
      balanceAfter: summary.client_balance_after,
    }));
    if (!opened) {
      toast.error("O navegador bloqueou a janela — permita popups para abrir o WhatsApp");
      return;
    }
    toast.success(summary.client_phone
      ? "WhatsApp aberto com o recibo pronto para enviar"
      : "Cliente sem telefone cadastrado — escolha o contato no WhatsApp");
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 flex items-start justify-center overflow-y-auto p-4 pointer-events-auto" data-testid="payment-result-dialog">
      <div className="bg-zinc-950 border border-emerald-400/25 rounded-lg w-full max-w-lg mt-14 mb-10 shadow-[0_0_60px_-15px_rgba(16,185,129,0.25)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} weight="fill" className="text-emerald-400" />
            <h2 className="font-display text-lg font-semibold">Pagamento registrado</h2>
          </div>
          <button data-testid="payment-result-x" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 transition-colors"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-center bg-emerald-400/5 border border-emerald-400/20 rounded-lg py-4">
            <p data-testid="payment-result-amount" className="font-display text-3xl font-bold text-emerald-400">
              {fmtBRL(summary.amount_received)} <span className="text-base font-medium text-emerald-300/80">recebidos</span>
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {summary.client_name} · {summary.payment_method} · {fmtDate(summary.payment_date)}
            </p>
          </div>

          {summary.orders?.length > 0 && (
            <div className="border border-zinc-800 rounded overflow-hidden" data-testid="payment-result-orders">
              <div className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 bg-zinc-900/60 border-b border-zinc-800">
                Onde o valor foi aplicado
              </div>
              {summary.orders.map((o) => (
                <div key={o.order_code} data-testid={`payment-result-order-${o.order_code}`}
                  className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800/60 last:border-b-0 text-sm">
                  <div>
                    <span className="text-zinc-200 font-medium">Pedido #{o.order_code}</span>
                    <span className="text-zinc-500"> — abatido </span>
                    <span className="text-emerald-400 font-medium">{fmtBRL(o.applied)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-right">
                    {o.balance_after > 0.009 ? (
                      <span className="text-amber-400 text-xs">restam <strong>{fmtBRL(o.balance_after)}</strong></span>
                    ) : (
                      <span className="text-emerald-400 text-xs font-medium">Liquidado ✓</span>
                    )}
                    {withReceipt.length > 1 && o.payment_id && (
                      <ReceiptMenu paymentId={o.payment_id} testId={`payment-result-receipt-${o.order_code}`}
                        filename={`comprovante_${o.order_code || "pagamento"}.pdf`} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {summary.leftover_credit > 0.009 && (
            <div data-testid="payment-result-credit"
              className="flex items-center gap-2 text-sm border border-emerald-400/25 bg-emerald-400/5 rounded px-3 py-2.5">
              <Wallet size={16} className="text-emerald-400 shrink-0" />
              <span className="text-zinc-300">
                <strong className="text-emerald-400">{fmtBRL(summary.leftover_credit)}</strong> ficou como crédito disponível
                <span className="text-zinc-500"> (crédito total: {fmtBRL(summary.client_credit_total)})</span>
              </span>
            </div>
          )}

          <div className="border border-zinc-800 rounded-lg p-4">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Saldo devedor total do cliente</p>
            <div className="flex items-center justify-center gap-3">
              <span data-testid="payment-result-before" className="font-display text-lg font-semibold text-zinc-400 line-through decoration-zinc-600">
                {fmtBRL(summary.client_balance_before)}
              </span>
              <ArrowRight size={18} className="text-emerald-400" />
              <span data-testid="payment-result-after" className={`font-display text-2xl font-bold ${summary.client_balance_after > 0.009 ? "text-rose-400" : "text-emerald-400"}`}>
                {fmtBRL(summary.client_balance_after)}
              </span>
            </div>
            {diff > 0.009 && (
              <p className="text-center text-xs text-zinc-500 mt-1.5">redução de {fmtBRL(diff)} na dívida</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {singleReceipt && (
              <ReceiptMenu variant="button" paymentId={singleReceipt.payment_id}
                testId="payment-result-receipt-button" label="Baixar comprovante"
                filename={`comprovante_${singleReceipt.order_code || "pagamento"}.pdf`} />
            )}
            <button data-testid="payment-result-whatsapp-button" onClick={sendWhatsApp}
              className="flex-1 inline-flex items-center justify-center gap-1.5 border border-green-500/40 text-green-400 hover:bg-green-500/10 text-sm font-medium py-2.5 rounded transition-colors">
              <WhatsappLogo size={17} weight="fill" /> WhatsApp
            </button>
            <button data-testid="payment-result-close" onClick={onClose}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium py-2.5 rounded transition-colors">
              Entendi
            </button>
          </div>
          {withReceipt.length > 1 && (
            <p className="text-[11px] text-zinc-500 text-center -mt-2">
              Use o ícone <Receipt size={11} className="inline text-emerald-400" /> em cada pedido para baixar o recibo daquele pagamento.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
